import { authHeaders } from './auth.js';
import { COPY } from './copy.js';
/** Fetching the chatbot's appearance and copy. */

const FALLBACK = {
  bot_name: 'Chat',
  welcome_message: '',
  avatar_url: null,
  accent_color: '#2F5BEA',
  position: 'bottom-right',
  locale: null,
  supported_locales: [],
  live_chat_enabled: false,
  // Support hours as windows in UTC (ADR-0065); null without hours.
  support_hours: null,
  // 'human_only' when the plan's AI conversations ran out and someone of the
  // team is available (ADR-0055 §4): the widget offers only a person.
  mode: 'bot',
  // 'support' for the platform's support assistant (ADR-0072): with nobody
  // available it opens a ticket instead of leaving a message (ADR-0078).
  audience: 'visitors',
  error_message: '',
  // Widget design (ADR-0059). Drupal sends them resolved; these only apply
  // when nothing arrived at all (a network blip, an older backend).
  greeting: '',
  rotating_phrases: [],
  subtitle: '',
  launcher_mode: 'greeting',
  launcher_image_url: null,
  show_branding: false,
  branding_label: '',
  branding_url: '',
  // Our "Report abuse" form in the visitor's language (ADR-0095).
  report_url: '',
  // The authorization asked before chatting (ADR-0089), or null.
  consent: null,
  // The dictation button while talking to the assistant (ADR-0101).
  voice_input: false,
  // The widget's texts translated by the backend, for a language the client
  // configured that the widget has no copy of its own for (ADR-0108).
  copy: null,
  // The "leave a message" form's own parts and the WhatsApp number for this
  // language (ADR-0110); drawn by contact.js, which checks their shape.
  contact_form: null,
  whatsapp: null,
};

/**
 * A `403` means the backend could not resolve any tenant at all — the
 * site_key is unknown, unauthorized for this domain, or its chatbot is
 * suspended. Unlike a network blip (below), this isn't "our side" failing;
 * it's the integration itself being wrong, same treatment `loader.js`
 * already gives a *missing* data-site-key: warn once, mount nothing.
 */
export async function loadConfig(apiBase, siteKey, locale) {
  const url = new URL('/api/widget-config', apiBase);
  url.searchParams.set('site_key', siteKey);
  if (locale) url.searchParams.set('locale', locale);

  let response;
  try {
    response = await fetch(url, { credentials: 'omit', headers: authHeaders() });
  } catch {
    // A real network blip on our side should degrade the widget's
    // appearance, not leave a dead button on a paying customer's site.
    return { ...FALLBACK, degraded: true };
  }

  if (response.status === 403) {
    const body = await response.json().catch(() => ({}));
    return { unauthorized: true, consoleMessage: body.console_message || '' };
  }
  if (!response.ok) return { ...FALLBACK, degraded: true };
  return mergeWithFallback(await response.json());
}

/**
 * A plain `{ ...FALLBACK, ...json }` spread only covers a *missing* key —
 * an empty string is still a value, so it would overwrite a real default
 * with nothing. Found this rendering a Chatbot whose accent_color was set
 * to "" (never configured in Drupal yet): `background: ${accent}` in
 * styles.js became invalid CSS, and white text landed on a transparent
 * background — a real, invisible-message bug, not a hypothetical one.
 */
function mergeWithFallback(remote) {
  const merged = { ...FALLBACK };
  for (const key of Object.keys(FALLBACK)) {
    const value = remote?.[key];
    const empty = value === '' || value === null || value === undefined
      || (Array.isArray(value) && value.length === 0);
    if (!empty) {
      merged[key] = value;
    }
  }
  const consent = merged.consent;
  merged.consent = consent && typeof consent.version === 'string' && typeof consent.message_html === 'string'
    // Every language's current version (ADR-0089): an authorization given
    // in one holds in the others.
    ? { ...consent, versions: Array.isArray(consent.versions) ? consent.versions.filter((v) => typeof v === 'string') : [] }
    : null;
  merged.voice_input = merged.voice_input === true;
  merged.copy = cleanCopy(merged.copy);
  return merged;
}

/** Only plain texts for keys the widget knows; anything else is dropped. */
function cleanCopy(copy) {
  if (!copy || typeof copy !== 'object' || Array.isArray(copy)) return null;
  const clean = {};
  for (const [key, value] of Object.entries(copy)) {
    if (Object.hasOwn(COPY.en, key) && typeof value === 'string' && value.trim() !== '' && value.length <= 500) {
      clean[key] = value;
    }
  }
  return Object.keys(clean).length ? clean : null;
}

export { FALLBACK, mergeWithFallback };
